/**
 * Environment Configuration and Validation
 *
 * This module validates required environment variables on app startup
 * and provides a type-safe configuration interface.
 *
 * IMPORTANT:
 * - All required env vars must be present or the app will fail gracefully
 * - Optional vars have defaults or can be undefined
 * - This prevents runtime errors from missing configuration
 */

import { logger } from './logger';

// Environment types
export type Environment = 'development' | 'production' | 'test';

// Configuration interface
export interface Config {
  supabase: {
    url: string;
    anonKey: string;
  };
  // NO `anthropic` SECTION, AND THERE MUST NEVER BE ONE AGAIN (#3).
  //
  // This used to hold `apiKey`, read from VITE_ANTHROPIC_API_KEY. Vite inlines
  // every VITE_-prefixed value into the client bundle at build time — that is
  // what the prefix means, not a misconfiguration — so the key was published to
  // every visitor of cpq-system.pages.dev and had to be revoked.
  //
  // The key now lives as a Supabase secret and is read only by the
  // `anthropic-proxy` Edge Function. A `VITE_` variable can never hold a
  // credential; see docs/architecture/SECURITY_TODO.md.
  app: {
    name: string;
    version: string;
    env: Environment;
  };
  env: Environment;
  isDevelopment: boolean;
  isProduction: boolean;
  devMode: boolean;
  apiUrl: string;
  features: {
    aiImport: boolean;
    analytics: boolean;
    debugMode: boolean;
  };
}

/**
 * Get required environment variable
 * Throws error if not found to prevent app startup with missing config
 */
function getRequiredEnv(key: string): string {
  const value = import.meta.env[key];

  if (
    !value ||
    value === '' ||
    value.includes('placeholder') ||
    value.includes('your-')
  ) {
    const error = `Missing or invalid required environment variable: ${key}

Please check your .env.local file:
1. Copy .env.example to .env.local if you haven't already
2. Fill in the actual value for ${key}
3. Restart the development server

See README.md for setup instructions.`;

    logger.error(`Environment validation failed: ${key}`);
    throw new Error(error);
  }

  return value;
}

/**
 * Get optional environment variable
 * Returns default value if not found
 */
function getOptionalEnv(key: string, defaultValue: string = ''): string {
  const value = import.meta.env[key];
  return value || defaultValue;
}

/**
 * Validate environment configuration
 * Called once during app initialization
 */
function validateConfig(): Config {
  logger.info('Validating environment configuration...');

  try {
    // Required: Supabase configuration
    const supabaseUrl = getRequiredEnv('VITE_SUPABASE_URL');
    const supabaseAnonKey = getRequiredEnv('VITE_SUPABASE_ANON_KEY');

    // The Anthropic key is deliberately absent. Whether one is configured is
    // now only knowable server-side, so there is nothing to validate or warn
    // about here — the proxy answers "Claude AI is not configured. Please add
    // your API key in Settings." at the moment of use, which is when the user
    // can actually act on it.
    //
    // The warning that used to live here has not been lost, only moved to where
    // it can be true: it once claimed Excel and CSV still worked without a key,
    // which was false (#94 — every format goes through Claude), and after that
    // it warned correctly but from a place that could no longer see the answer.

    // Environment detection
    const mode = import.meta.env.MODE || 'development';
    const env: Environment =
      mode === 'production'
        ? 'production'
        : mode === 'test'
          ? 'test'
          : 'development';

    // Development flags
    const devMode = getOptionalEnv('VITE_DEV_MODE', 'false') === 'true';
    const apiUrl = getOptionalEnv('VITE_API_URL', 'http://localhost:3001');

    // Application metadata
    const appName = getOptionalEnv('VITE_APP_NAME', 'CPQ System');
    const appVersion = getOptionalEnv('VITE_APP_VERSION', '1.0.0');

    // Feature flags
    const aiImportEnabled =
      getOptionalEnv('VITE_ENABLE_AI_IMPORT', 'true') === 'true';
    const analyticsEnabled =
      getOptionalEnv('VITE_ENABLE_ANALYTICS', 'false') === 'true';
    const debugModeEnabled =
      getOptionalEnv('VITE_DEBUG_MODE', 'false') === 'true';

    const config: Config = {
      supabase: {
        url: supabaseUrl,
        anonKey: supabaseAnonKey,
      },
      app: {
        name: appName,
        version: appVersion,
        env,
      },
      env,
      isDevelopment: env === 'development',
      isProduction: env === 'production',
      devMode,
      apiUrl,
      features: {
        aiImport: aiImportEnabled,
        analytics: analyticsEnabled,
        debugMode: debugModeEnabled,
      },
    };

    logger.info(`✅ ${appName} v${appVersion}`);
    logger.info(`Environment: ${env}`);
    logger.info(`Development mode: ${devMode}`);
    logger.info(`AI Import: ${aiImportEnabled ? 'Enabled' : 'Disabled'}`);
    logger.info(`Analytics: ${analyticsEnabled ? 'Enabled' : 'Disabled'}`);
    logger.info(`Debug Mode: ${debugModeEnabled ? 'Enabled' : 'Disabled'}`);
    logger.info('Environment configuration validated successfully');

    return config;
  } catch (error) {
    // Log error and re-throw to prevent app from starting
    logger.error('Environment configuration validation failed', error);
    throw error;
  }
}

/**
 * Validate API key format
 */
export function isValidApiKey(
  key: string | null | undefined,
  prefix: string
): boolean {
  return (
    !!key &&
    key.startsWith(prefix) &&
    !key.includes('your-') &&
    !key.includes('placeholder')
  );
}

/**
 * Validate Supabase URL format
 */
export function isValidSupabaseUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      parsed.hostname.endsWith('.supabase.co') ||
      parsed.hostname === 'localhost'
    );
  } catch {
    return false;
  }
}

// Validate and export configuration
// This will run once when the module is imported
// If validation fails, the app won't start
export const config: Config = validateConfig();

// Export for tests
export { validateConfig, getRequiredEnv, getOptionalEnv };
